import { Component, OnInit, Input, forwardRef, OnDestroy } from '@angular/core';
import { ApiService } from '../../../../../services/api.service';
import { AbstractControl, ControlValueAccessor, FormBuilder, FormGroup, NG_ASYNC_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors, Validator, Validators } from '@angular/forms';
import { Buyer, BuyerBranch } from '../../document-issue.model';
import { combineLatest, Observable, Subscription } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';


@Component({
  selector: 'app-buyer-form',
  templateUrl: './buyer-form.component.html',
  styleUrls: ['./buyer-form.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => BuyerFormComponent),
      multi: true
    },
    {
      provide: NG_ASYNC_VALIDATORS,
      useExisting: forwardRef(() => BuyerFormComponent),
      multi: true
    }
  ]
})
export class BuyerFormComponent implements OnInit, OnDestroy, ControlValueAccessor, Validator {
  onChange: any = () => { };
  onTouched: any = () => { };

  @Input() branchID: string;
  @Input() isSubmitted: boolean;
  @Input() documentInformation;
  @Input() docProcessType: any;

  username = localStorage.getItem('username');

  buyerForm: FormGroup;
  subscription: Subscription;

  countries: any[];
  buyers: Buyer[];

  isBuyerHeadOffice = false;
  refreshDropdown = false;

  constructor(
    private formBuilder: FormBuilder,
    private apiService: ApiService,
    private translateService: TranslateService
  ) { }

  ngOnInit() {
    this.translateService.onLangChange.subscribe(() => {
      this.translateDropdown();
    });

    this.createBuyerForm();
    // any time the inner form changes update the parent of any change
    this.createSubscriptions();
    this.getCountries();
    this.getBuyer();
  }

  translateDropdown(): void {
    this.refreshDropdown = true;
    setTimeout(() => {
      this.refreshDropdown = false;
    }, 1);
  }

  get currentLang(): string {
    return this.translateService.currentLang;
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  writeValue(value: any) {
    if (value && value.name) {
      this.buyerForm.setValue(value);
      this.onChange(value);
      this.onTouched();
    }
  }

  registerOnChange(fn: any) {
    this.onChange = fn;
  }

  registerOnTouched(fn: any) {
    this.onTouched = fn;
  }

  // communicate the inner form validation to the parent form
  validate(_control: AbstractControl): Promise<ValidationErrors | null> | Observable<ValidationErrors | null> {
    return new Promise(resolve =>
      this.buyerForm.valid ? resolve(null) : resolve({ buyerForm: { valid: false } })
    );
  }

  createSubscriptions() {
    // Subscribe to valueChanges and statusChanges
    this.subscription = combineLatest([this.buyerForm.valueChanges, this.buyerForm.statusChanges]).subscribe(
      () => {
        this.onChange(this.buyerForm.getRawValue());
        this.onTouched();
      });
  }

  getBuyer() {
    this.apiService.getNewBuyer().subscribe(
        (res: any) => {
          this.buyers = res || [];

          if (this.branchID) {
            const customer = this.buyers.find(buyer => !!(buyer.branch.find(branch => branch.id.toString() === this.branchID)));

            this.setBuyer(customer);
          } else if (this.buyers.length === 1) {
            this.setBuyer(this.buyers[0]);
          }
        }
      );
  }

  getCountries() {
    this.apiService.getCountry().subscribe(
        (res: any) => {
          this.countries = res;
        }
      );
  }

  createBuyerForm() {
    this.buyerForm = this.formBuilder.group({
      name: [null, Validators.required],
      taxID: [null],
      branchCode: [null],
      branchName: [null],
      address: [null, Validators.required],
      postalCode: [null, Validators.required],
      country: ['TH', Validators.required],
      email: [null],
      ccEmail: [null]
    });
  }

  get buyerBranches() {
    const buyer = this.buyers ? this.buyers.find(buyerItem => buyerItem.name === this.buyerName.value) : null;

    return buyer ? buyer.branch : [];
  }

  get buyerName() {
    return this.buyerForm.get('name');
  }

  get buyerTaxID() {
    return this.buyerForm.get('taxID');
  }

  get buyerBranchCode() {
    return this.buyerForm.get('branchCode');
  }

  get buyerBranchName() {
    return this.buyerForm.get('branchName');
  }

  get buyerAddress() {
    return this.buyerForm.get('address');
  }

  get buyerPostalCode() {
    return this.buyerForm.get('postalCode');
  }

  get buyerCountry() {
    return this.buyerForm.get('country');
  }

  get buyerEmail() {
    return this.buyerForm.get('email');
  }

  get buyerCcEmail() {
    return this.buyerForm.get('ccEmail');
  }

  setBuyer(buyer: Buyer) {
    if (this.docProcessType) {
      const documentTypeReissue: boolean = !!this.documentInformation.buyer;
      const buyerDocumentInfo =
        documentTypeReissue ? buyer : this.documentInformation;

      this.buyerName.setValue(documentTypeReissue ? buyerDocumentInfo.name : buyerDocumentInfo.buyer_name);
      this.buyerTaxID.setValue(
        documentTypeReissue ? buyerDocumentInfo.tax_id.substring(0, 13) : buyerDocumentInfo.tax_id
      );

      const buyerBranchDocumentInfo = {
        branch_code: documentTypeReissue ? buyerDocumentInfo.tax_id.substring(13, 18) : buyerDocumentInfo.branch_code,
        branch_name: buyerDocumentInfo.branch_name,
        address: buyerDocumentInfo.address,
        post_code: buyerDocumentInfo.postal_code,
        country_code: documentTypeReissue ? buyerDocumentInfo.country_code : buyerDocumentInfo.country,
        email: buyerDocumentInfo.email,
        cc_email: buyerDocumentInfo.cc_email,
      };

      this.setBuyerBranch(buyerBranchDocumentInfo);
      return;
    }

    if (this.buyers && this.buyers.includes(buyer)) {
      const name = buyer.name;
      const taxID = buyer.tax_id;

      this.buyerName.setValue(name);
      this.buyerTaxID.setValue(taxID);

      this.setBuyerBranch(null);

      if (buyer.branch.length === 1) {
        this.setBuyerBranch(buyer.branch[0]);
      }
    } else if (buyer.tax_id && buyer.tax_id.length === 13) {
      this.buyerName.setValue(null);
      this.setBuyerBranch(null);
      this.apiService.getExternalBuyerData({'tax_id': buyer.tax_id}).subscribe(
        (result) => {
          if (result) {
            this.buyerName.setValue(result.name);
            this.buyerForm.patchValue({
              address: result.address,
              country: 'TH',
              postalCode: result.post_code
            });
          }
        },
        (error) => {}
      );
    }
  }

  setBuyerBranch(branch) {
    if (this.buyerBranches.includes(branch) || !branch) {
      const branchCode = branch ? branch.branch_code : null;
      const branchName = branch ? branch.branch_name : null;
      const address = branch ? branch.address : null;
      const postalCode = branch ? branch.post_code : null;
      const country = branch ? branch.country_code : null;
      const email = branch ? branch.email : null;
      const ccEmail = branch ? branch.cc_email : null;

      this.buyerForm.patchValue({
        branchCode: branchCode,
        branchName: branchName,
        address: address,
        postalCode: postalCode,
        country: country,
        email: email,
        ccEmail: ccEmail
      });
    } else if (this.buyerBranchCode.value === '00000') {
      this.buyerBranchName.setValue('HQ');
    }

    this.isBuyerHeadOffice = this.buyerBranchCode.value === '00000';
    this.isBuyerHeadOffice ? this.buyerBranchName.disable() : this.buyerBranchName.enable();
  }

  setBuyerHeadOffice() {
    if (this.isBuyerHeadOffice) {
      const branch = this.buyerBranches.find(buyerBranche => buyerBranche.branch_code === '00000');

      if (branch) {
        this.setBuyerBranch(branch);
      } else {
        this.buyerBranchCode.setValue('00000');
        this.buyerBranchName.setValue('HQ');
      }

      this.buyerBranchName.disable();
    } else {
      this.setBuyerBranch(null);
      this.buyerBranchName.enable();
    }
  }
}



// WEBPACK FOOTER //
// /opt/app/src/app/features/document/document-issue/components/buyer-form/buyer-form.component.ts